export class IpWebComponent extends HTMLElement {
    constructor(templateNode: HTMLTemplateElement) {
        super();
        this.attachShadow({ mode: 'open' });
        this.shadowRoot?.appendChild(templateNode.content.cloneNode(true));
    }

    protected getProp<T extends string | undefined>(attributeName: string, defaultValue?: T) {
        const result = this.getAttribute(attributeName) ?? defaultValue;
        return result as T extends string ? string : string | undefined;
    }

    protected getPropAsNumber<T extends number | undefined>(attributeName: string, defaultValue?: T) {
        const attr = this.getAttribute(attributeName);
        const result = attr ? Number.parseFloat(attr) : defaultValue;
        return result as T extends number ? number : number | undefined;
    }

    protected getPropAsBoolean<T extends boolean | undefined>(attributeName: string, defaultValue?: T) {
        const attr = this.getAttribute(attributeName);
        const result = attr ? attr.toLowerCase() === 'true' : defaultValue ?? null;
        return result as T extends boolean ? boolean : boolean | undefined;
    }

    protected query<T extends Element>(selector: string) {
        return (this.shadowRoot?.querySelector(selector) ?? null) as T | null;
    }

    /**
     * This function is unsafe, as it will throw an error if the element is not found.
     * Use this, if you are sure the element is there to avoid the null check.
     * @param selector
     * @returns
     */
    protected queryUnsafe<T extends Element>(selector: string) {
        const elm = this.shadowRoot?.querySelector(selector);
        if (!elm) {
            throw new Error(`Element with selector "${selector}" not found.`);
        }
        return elm as T;
    }

    protected queryAll<T extends Element>(selector: string) {
        const res = this.shadowRoot?.querySelectorAll(selector);
        return res ? ([...res] as T[]) : [];
    }

    protected queryById<T extends Element>(id: string) {
        return (this.shadowRoot?.getElementById(id) ?? null) as T | null;
    }

    /**
     * Takes the value of the prop and sets it as the innerText of the element.
     * @param selector The selector of the element to set the content of. It uses queryUnsafe, so it will throw an error if the element is not found.
     * @param attributeName The name of the prop attribute to get the value from.
     * @param defaultValue The default value to use if the prop is not set.
     */
    protected setContentFromProp<T extends string | undefined>(
        selector: string,
        attributeName: string,
        defaultValue?: T
    ) {
        const elm = this.queryUnsafe<HTMLElement>(selector);
        const content = this.getProp(attributeName, defaultValue);
        if (content) {
            elm.innerText = content;
        }
    }
}
